// src/components/ShopConfig/CounterAutocomplete.js
import React, { useState, useEffect, useRef } from "react";
import { Input } from "../ui/input";
import { Store, Search, Loader2, Plus, AlertCircle } from "lucide-react";
import { useAppContext } from "../../context/AppContext";
import { fetchCounters } from "../../services/api";

function CounterAutocomplete({ value, onChange, onSelectCounter }) {
  const [displayValue, setDisplayValue] = useState("");
  const [counters, setCounters] = useState([]);
  const [filteredCounters, setFilteredCounters] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [apiRetryCount, setApiRetryCount] = useState(0);
  const [initialized, setInitialized] = useState(false);
  const MAX_RETRIES = 3;

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const { actions } = useAppContext();

  // Initialize displayValue when component loads with a value
  useEffect(() => {
    // Skip if already initialized or no value is provided
    if (initialized || !value) return;

    const fetchInitialCounterData = async () => {
      setIsLoading(true);
      try {
        console.log("Fetching counter data for", value);
        const countersData = await fetchCounters(1, 20);
        console.log("Fetched counters:", countersData);

        // Find the counter with matching activation key
        const matchingCounter = countersData.find(
          (c) => c.activationKey === value
        );

        if (matchingCounter) {
          console.log("Found matching counter:", matchingCounter);
          // Set the display value to the counter name
          setDisplayValue(matchingCounter.counterName);
          setCounters(countersData);
        } else {
          console.log("No matching counter found for", value);
          // If no match is found, use the value as the display value
          setDisplayValue(value);
        }

        // Mark as initialized to prevent re-running
        setInitialized(true);
      } catch (err) {
        console.error("Error loading counter for template name:", err);
        // Fall back to using the value as display value
        setDisplayValue(value);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialCounterData();
  }, [value, initialized]);

  // Reset initialized state when value changes
  useEffect(() => {
    if (value && initialized) {
      // If the value changes after initialization,
      // we need to reset the initialization state
      setInitialized(false);
    }
  }, [value]);

  // Fetch counters when dropdown is opened
  useEffect(() => {
    const fetchCounterData = async () => {
      if (isOpen) {
        setIsLoading(true);
        setError(null);

        try {
          // Search using the display value (counter name) that user sees/types
          const countersData = await fetchCounters(1, 10, displayValue);
          setCounters(countersData);
          setFilteredCounters(countersData);
          setError(null);
          setApiRetryCount(0); // Reset retry count on success
        } catch (err) {
          console.error("Error fetching counters:", err);

          // Limited retry logic
          if (apiRetryCount < MAX_RETRIES) {
            setApiRetryCount((prev) => prev + 1);
            setError(
              `Error loading counters. Retry attempt ${
                apiRetryCount + 1
              }/${MAX_RETRIES}...`
            );
          } else {
            setError("Failed to load counters after multiple attempts.");
            actions.setNotification(
              "Failed to load counters after multiple attempts.",
              "error"
            );
          }
        } finally {
          setIsLoading(false);
        }
      }
    };

    // Don't fetch if we've exceeded max retries
    if (isOpen && apiRetryCount < MAX_RETRIES) {
      const debounceTimer = setTimeout(fetchCounterData, 300);
      return () => clearTimeout(debounceTimer);
    }
  }, [isOpen, apiRetryCount, displayValue, actions]);

  // Reset retry count when search term changes
  useEffect(() => {
    setApiRetryCount(0);
  }, [displayValue]);

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !inputRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle changes to the input field
  const handleInputChange = (e) => {
    const newDisplayValue = e.target.value;
    setDisplayValue(newDisplayValue);

    // Reset the api retry count when input changes
    setApiRetryCount(0);

    // Open dropdown when typing
    if (!isOpen) {
      setIsOpen(true);
    }

    // For free text input (not selecting from dropdown),
    // we use the same value for both display and actual value
    onChange(newDisplayValue);
  };

  // Handle selecting a counter from the dropdown
  const handleCounterSelect = (counter) => {
    // Save the activation key as the actual value
    onChange(counter.activationKey);

    // Show the counter name in the input field
    setDisplayValue(counter.counterName);
    setIsOpen(false);

    // Call the parent callback with the selected counter
    if (onSelectCounter) {
      onSelectCounter(counter);
    }
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    setApiRetryCount(0); // Reset retry count when focusing
  };

  // Manual retry function
  const handleRetry = () => {
    setApiRetryCount(0); // Reset retry count to trigger a new fetch
  };

  // Highlight matching text in search results
  const highlightMatch = (text, query) => {
    if (!query.trim()) return text;

    const regex = new RegExp(
      `(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "gi"
    );
    return text.replace(
      regex,
      '<mark class="bg-yellow-200 rounded-sm px-0.5">$1</mark>'
    );
  };

  return (
    <div className="relative">
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Store className="h-5 w-5 text-gray-400" />
        </div>
        <Input
          ref={inputRef}
          id="templateName"
          name="templateName"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          className="pl-10"
          placeholder="Search for a counter..."
          autoComplete="off"
        />
      </div>

      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto"
        >
          {isLoading ? (
            <div className="flex items-center justify-center p-4 text-gray-500">
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              Loading counters...
            </div>
          ) : error ? (
            <div className="p-4 text-red-500 text-sm">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <p>{error}</p>
                  {apiRetryCount >= MAX_RETRIES && (
                    <button
                      onClick={handleRetry}
                      className="mt-2 px-3 py-1 bg-red-100 text-red-700 rounded text-xs font-medium hover:bg-red-200"
                    >
                      Try Again
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : filteredCounters.length === 0 ? (
            <div className="p-4 text-gray-500 text-sm">
              No counters found. Try a different search term or create a new
              template.
            </div>
          ) : (
            <ul className="py-1">
              {filteredCounters.map((counter) => (
                <li
                  key={counter.counterGUID}
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-start"
                  onClick={() => handleCounterSelect(counter)}
                >
                  <div>
                    <div
                      className="font-medium text-gray-900"
                      dangerouslySetInnerHTML={{
                        __html: highlightMatch(
                          counter.counterName,
                          displayValue
                        ),
                      }}
                    />
                    <div className="text-xs text-gray-500">
                      <span className="font-medium">Shop:</span>{" "}
                      {counter.shopName} |{" "}
                      <span className="font-medium">Org:</span>{" "}
                      {counter.organizationName} |{" "}
                      <span className="font-medium">Counter key:</span>{" "}
                      {counter.activationKey}
                    </div>
                  </div>
                </li>
              ))}

              {displayValue &&
                !filteredCounters.some(
                  (c) =>
                    c.counterName.toLowerCase() === displayValue.toLowerCase()
                ) && (
                  <li
                    className="px-4 py-2 border-t border-gray-100 hover:bg-blue-50 cursor-pointer"
                    onClick={() => {
                      // For creating a new entry, both display and value are the same
                      onChange(displayValue);
                      setIsOpen(false);
                    }}
                  >
                    <div className="flex items-center text-blue-600">
                      <Plus className="h-4 w-4 mr-2" />
                      Create new template:{" "}
                      <span className="font-semibold ml-1">{displayValue}</span>
                    </div>
                  </li>
                )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default CounterAutocomplete;
